import React, { useState } from 'react';
import { MarketCapCategory, PreInflectionScreenResult, PreInflectionScreenOutput } from '../types/geminiTypes';
import { PreInflectionScreenResultsMap } from '../types/analysis';
import { Loader2, Search, ArrowRight, ShieldAlert, TrendingUp } from 'lucide-react';
import { auth } from '../firebase';

interface PreInflectionScreenerProps {
  onRunDetector: (ticker: string) => void;
  results: PreInflectionScreenResultsMap;
  selectedCap: MarketCapCategory;
  onCapChange: (cap: MarketCapCategory) => void;
  onResultsChange: (output: PreInflectionScreenOutput) => void;
}

export function PreInflectionScreener({ onRunDetector, results, selectedCap, onCapChange, onResultsChange }: PreInflectionScreenerProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('Scanning universe...');

  const current = results[selectedCap];

  const handleRunScreen = async () => {
    setLoading(true);
    setError(null);
    setLoadingMessage('Scanning universe...');
    try {
      const token = await auth.currentUser?.getIdToken();
      const url = `/api/pre-inflection-screen-stream?category=${selectedCap}`;
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok || !response.body) throw new Error('Screen request failed');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.message) setLoadingMessage(event.message);
            if (event.stage === 'done' && event.result) {
              onResultsChange(event.result);
            }
            if (event.stage === 'error') {
              throw new Error(event.error || 'Screen failed');
            }
          } catch (parseErr: any) {
            if (parseErr.message && parseErr.message !== 'Screen failed') continue;
            throw parseErr;
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Screen failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-20">
      <div className="oi-card overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-medium text-ink capitalize font-serif tracking-tight flex items-center gap-2">
                <Search className="w-5 h-5 text-oxblood" />
                Find Pre-Inflection Candidates
              </h2>
              <p className="text-sm text-ink-500 mt-1 max-w-2xl">
                Scan for businesses with accelerating revenue, rising margins, and durable ROIC—before the multiple reflects the inflection.
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-ink-500 mr-2">Market Cap:</span>
              {(['small', 'mid', 'large'] as MarketCapCategory[]).map(cat => (
                <button
                  key={cat}
                  onClick={() => onCapChange(cat)}
                  disabled={loading}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    selectedCap === cat
                      ? 'bg-oxblood text-paper'
                      : 'bg-surface-2 text-ink-600 hover:bg-surface disabled:opacity-50'
                  }`}
                >
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 pt-4 border-t border-ink-200 bg-surface-2">
          <button
            onClick={handleRunScreen}
            disabled={loading}
            className="w-full md:w-auto px-8 py-3 bg-oxblood hover:bg-oxblood-800 disabled:bg-ink-300 disabled:cursor-not-allowed text-paper font-medium rounded-md flex items-center justify-center gap-2 transition-colors duration-200"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {loadingMessage}
              </>
            ) : (
              <>
                <TrendingUp className="w-5 h-5" />
                Run Screen
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-danger-50 text-danger-700 p-4 rounded-md flex items-start gap-3 border border-danger-100">
          <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {loading && (
        <div className="oi-card p-10">
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-ink-200">
            <Loader2 className="w-4 h-4 text-oxblood animate-spin shrink-0" />
            <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Screening In Progress</span>
          </div>
          <h3 className="text-lg font-medium text-ink mb-2">{loadingMessage}</h3>
          <p className="text-ink-500 max-w-sm text-sm">
            Fetching consecutive quarters for {selectedCap} cap stocks, calculating acceleration momentum, and screening out cyclical noise...
          </p>
        </div>
      )}

      {!loading && current.lastRun !== null && current.results.length === 0 && (
        <div className="oi-card p-10">
          <div className="mb-4 pb-4 border-b border-ink-200">
            <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Screening Complete</span>
          </div>
          <h3 className="text-lg font-medium text-ink mb-2">No Clear Candidates Found</h3>
          <p className="text-ink-500 max-w-md text-sm">
            None of the {current.scannedCount} screened stocks in this band successfully cleared the strict trend acceleration gates today. Try a different size band.
          </p>
        </div>
      )}

      {!loading && current.results.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-lg font-medium text-ink font-serif tracking-tight">
              Top Candidates ({current.results.length})
            </h3>
            <div className="text-right">
              <p className="text-sm text-ink-500">
                Scanned universe: {current.scannedCount}
              </p>
              {current.lastRun && (
                <p className="text-[10px] text-ink-500 mt-0.5">
                  Last run: {new Date(current.lastRun).toLocaleString(undefined, { 
                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' 
                  })}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-4">
            {current.results.map((result, i) => (
              <div 
                key={result.ticker}
                className="oi-card p-5 hover:border-oxblood/30 transition-colors duration-200"
              >
                <div className="flex flex-col lg:flex-row gap-5">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-md bg-oxblood-50 flex items-center justify-center border border-oxblood/20 mb-1">
                          <span className="font-medium text-oxblood font-mono text-sm">
                            {result.candidacyScore.toFixed(0)}
                          </span>
                        </div>
                        <span className="text-[10px] uppercase font-medium text-ink-500 tracking-wider">Score</span>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-xl font-medium font-serif tracking-tight text-ink">
                            {result.ticker}
                          </h4>
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-surface-2 text-ink-600">
                            {result.sector}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                            result.screenStage === 2 
                              ? 'bg-oxblood-50 text-oxblood' 
                              : 'bg-surface-2 text-ink-600'
                          }`}>
                            STAGE {result.screenStage}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <p className="text-sm text-ink-500 font-medium">{result.name}</p>
                          {result.currentPrice > 0 && (
                            <span className="font-mono text-sm font-medium text-ink shrink-0">${result.currentPrice.toFixed(2)}</span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2.5 py-1 rounded-md text-xs font-mono font-medium bg-surface-2 text-ink-600 border border-ink-200">
                            Accel: +{Math.round(result.signals.revenueAccelerationBps)} bps
                          </span>
                          <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${
                            result.signals.marginTrend === 'rising' ? 'bg-success-50 text-success-700 border-success-100' : 
                            result.signals.marginTrend === 'flat' ? 'bg-surface-2 text-ink-600 border-ink-200' :
                            'bg-danger-50 text-danger-700 border-danger-100'
                          }`}>
                            Margins: {result.signals.marginTrend}
                          </span>
                          <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${
                            result.signals.capitalEfficiencyTrend === 'rising' ? 'bg-success-50 text-success-700 border-success-100' : 
                            result.signals.capitalEfficiencyTrend === 'flat' ? 'bg-surface-2 text-ink-600 border-ink-200' :
                            'bg-danger-50 text-danger-700 border-danger-100'
                          }`}>
                            ROIC: {result.signals.capitalEfficiencyTrend}
                          </span>
                          <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${
                            result.signals.valuation === 'cheap' ? 'bg-success-50 text-success-700 border-success-100' : 
                            result.signals.valuation === 'inline' ? 'bg-surface-2 text-ink-600 border-ink-200' :
                            'bg-warning-50 text-warning-700 border-warning-100'
                          }`}>
                            Valuation: {result.signals.valuation}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-surface-2 rounded-md border border-ink-200 text-sm text-ink-600 italic border-l-4 border-l-oxblood">
                      "{result.rationale}"
                    </div>
                  </div>
                  
                  <div className="flex lg:flex-col justify-center shrink-0 border-t lg:border-t-0 lg:border-l border-ink-200 pt-4 lg:pt-0 lg:pl-5">
                    <button
                      onClick={() => onRunDetector(result.ticker)}
                      className="w-full group flex items-center justify-center gap-2 px-6 py-3 bg-oxblood hover:bg-oxblood-800 text-paper text-sm font-medium rounded-md transition-colors"
                    >
                      Run Detector
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
